import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface UbicacionData {
	nombreAsegurado: string
	email: string
	telefono: string
	ubicacionRiesgo: string
}

interface IntegralAgropecuarioUbicacionScreenProps {
	onNext: (data: UbicacionData) => void
	onBack: () => void
	data?: Partial<UbicacionData>
}

export function IntegralAgropecuarioUbicacionScreen({
	onNext,
	onBack,
	data,
}: IntegralAgropecuarioUbicacionScreenProps) {
	const [formData, setFormData] = useState<UbicacionData>({
		nombreAsegurado: data?.nombreAsegurado || '',
		email: data?.email || '',
		telefono: data?.telefono || '',
		ubicacionRiesgo: data?.ubicacionRiesgo || '',
	})

	const [errors, setErrors] = useState<
		Partial<Record<keyof UbicacionData, string>>
	>({})

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof UbicacionData, string>> = {}

		if (!formData.nombreAsegurado.trim()) {
			newErrors.nombreAsegurado = 'El nombre del asegurado es requerido'
		}
		if (!formData.email.trim()) {
			newErrors.email = 'El email es requerido'
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'El email no es válido'
		}
		if (!formData.telefono.trim()) {
			newErrors.telefono = 'El teléfono es requerido'
		}
		if (!formData.ubicacionRiesgo.trim()) {
			newErrors.ubicacionRiesgo = 'La ubicación del riesgo es requerida'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Asegurado
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Información de contacto y ubicación del establecimiento
					</p>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre del Asegurado / Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre completo o razón social"
							value={formData.nombreAsegurado}
							onChange={(e) =>
								setFormData({ ...formData, nombreAsegurado: e.target.value })
							}
							errorText={errors.nombreAsegurado}
						/>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Email"
								labelPosition="internal"
								size="l"
								placeholder="correo@ejemplo.com"
								type="email"
								value={formData.email}
								onChange={(e) =>
									setFormData({ ...formData, email: e.target.value })
								}
								errorText={errors.email}
							/>

							<InputText
								label="Teléfono"
								labelPosition="internal"
								size="l"
								placeholder="11 1234-5678"
								value={formData.telefono}
								onChange={(e) =>
									setFormData({ ...formData, telefono: e.target.value })
								}
								errorText={errors.telefono}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Ubicación del Riesgo
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Dirección completa del establecimiento agropecuario (localidad, partido, provincia)"
								value={formData.ubicacionRiesgo}
								onChange={(e) =>
									setFormData({ ...formData, ubicacionRiesgo: e.target.value })
								}
							/>
							{errors.ubicacionRiesgo && (
								<p className="text-sm text-red-500">{errors.ubicacionRiesgo}</p>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
